import React from "react";
import RecognitionGuideLayout from "@/components/RecognitionGuideLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { BarChart3, Heart, Users, TrendingUp, Target, DollarSign, CheckCircle, ArrowRight, ExternalLink, Plus, Minus } from "lucide-react";

export { Page }

function Page() {
  const impactAreas = [
    {
      title: "Engagement",
      description: "Recognition is one of the strongest levers to lift employee engagement. Gallup research shows employees who receive regular recognition are 4.6 times more likely to be engaged at work. Engaged employees contribute more, innovate more often, and drive better customer outcomes.",
      icon: Heart,
      links: [
        { text: "How Employee Recognition Impacts Engagement at Work", url: "/blog/how-employee-recognition-impacts-employee-engagement-at-work" },
        { text: "The Psychological Benefits of Recognition", url: "/blog/the-psychological-benefits-of-employee-recognition" }
      ]
    },
    {
      title: "Retention",
      description: "Turnover is expensive-and recognition reduces it. Employees who don't feel appreciated are twice as likely to say they'll quit within a year. Replacing them can cost 30-200% of their annual salary. Recognition sends a clear message: 'Your contributions matter here.' That sense of value keeps top performers loyal and reduces the revolving door effect.",
      icon: Users,
      links: [
        { text: "Recognition as a Tool for Reducing Employee Turnover", url: "/blog/recognition-as-a-tool-for-reducing-employee-turnover" },
        { text: "Retention Strategies Through Inclusion & Recognition", url: "/blog/retention-through-inclusion-recognizing-diverse-contributions" }
      ]
    },
    {
      title: "Productivity",
      description: "Recognition doesn't just increase effort-it sharpens focus. When employees are recognized for specific behaviors, they repeat them. That creates a cycle of improved quality and faster delivery.",
      icon: TrendingUp,
      links: [
        { text: "Boosting Morale and Productivity in the Workplace", url: "/blog/boosting-employee-morale-and-productivity-in-the-workplace" },
        { text: "How Points-Based Reward Systems Drive Productivity", url: "/blog/how-points-based-reward-systems-drive-engagement-and-productivity" }
      ]
    },
    {
      title: "Culture and Belonging",
      description: "Recognition makes company values visible and fosters inclusion. Employees who regularly give and receive recognition report higher feelings of belonging and trust within teams. In hybrid and global workplaces, this is especially critical to avoid isolation.",
      icon: Target,
      links: [
        { text: "The Importance of a Culture of Recognition", url: "/blog/the-importance-of-a-culture-of-recognition-in-the-workplace" },
        { text: "The Link Between Recognition and Team Collaboration", url: "/blog/the-link-between-employee-recognition-and-team-collaboration" }
      ]
    }
  ];

  const roiBenefits = [
    "Lower turnover costs",
    "Higher engagement survey scores",
    "Increased productivity and output",
    "Stronger culture and employer brand"
  ];

  return (
    <RecognitionGuideLayout
      title="Employee Recognition Impact"
      description="Learn how to measure employee recognition program impact and ROI. Discover key metrics, measurement strategies, and proven business results from effective recognition programs."
      currentStep={5}
    >
      <div className="space-y-12">
        {/* Header */}
        <div className="space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium border border-primary/20">
            <BarChart3 className="w-4 h-4" />
            Step 5 of 6
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
            The Impact of Employee Recognition
          </h1>
          
          <p className="text-lg text-muted-foreground max-w-4xl leading-relaxed">
            Recognition isn't just about making people feel good-it creates measurable outcomes across engagement, retention, productivity, and culture. When employees feel valued, they show up with more energy, more commitment, and more resilience. Organizations that invest in recognition consistently see stronger business results compared to those that don't.
          </p>
        </div>

        {/* Impact Areas Section */}
        <section className="space-y-8">
          <div className="text-center space-y-3">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              Four Key Areas of Impact
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Recognition drives measurable results across these critical business dimensions
            </p>
          </div>

          <div className="grid gap-8">
            {impactAreas.map((area, index) => {
              const IconComponent = area.icon;
              return (
                <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-border/60 bg-card/50 backdrop-blur-sm">
                  <CardContent className="p-6 md:p-8">
                    <div className="flex flex-col md:flex-row gap-6">
                      <div className="flex items-start">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 border border-primary/20">
                          <IconComponent className="w-6 h-6 text-primary" />
                        </div>
                      </div>
                      
                      <div className="flex-1 space-y-4">
                        <div>
                          <h3 className="text-xl font-semibold text-foreground mb-3">
                            {area.title}
                          </h3>
                          <p className="text-muted-foreground leading-relaxed">
                            {area.description}
                          </p>
                        </div>
                        
                        <div className="flex flex-col sm:flex-row gap-2 text-sm">
                          {area.links.map((link, linkIndex) => (
                            <div key={linkIndex} className="flex items-center gap-2">
                              <ExternalLink className="w-4 h-4 text-primary shrink-0" />
                              <a 
                                href={link.url} 
                                className="text-primary hover:text-primary/80 transition-colors font-medium underline underline-offset-2"
                              >
                                {link.text}
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* ROI Section */}
        <section className="space-y-8">
          <div className="text-center space-y-3">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              The ROI of Recognition
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Recognition isn't just good for people-it's good for business
            </p>
          </div>

          <Card className="border-border/60 bg-card/50 backdrop-blur-sm">
            <CardContent className="p-6 md:p-8">
              <div className="space-y-6">
                <p className="text-muted-foreground leading-relaxed max-w-3xl">
                  Organizations with high recognition cultures are more profitable, deliver higher customer satisfaction, and show stronger resilience during downturns.
                </p>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  {roiBenefits.map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2 shrink-0"></div>
                      <span className="text-muted-foreground text-sm">{benefit}</span>
                    </div>
                  ))}
                </div>
                
                <div className="flex items-center gap-2 text-sm pt-2">
                  <ExternalLink className="w-4 h-4 text-primary" />
                  <a 
                    href="/blog/how-to-measure-the-roi-of-employee-recognition-programs" 
                    className="text-primary hover:text-primary/80 transition-colors font-medium underline underline-offset-2"
                  >
                    How to Measure the ROI of Recognition Programs
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* FAQ Section */}
        <section className="space-y-8">
          <div className="text-center space-y-3">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              Frequently Asked Questions
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Common questions about recognition program impact and measurement
            </p>
          </div>

          <div className="space-y-4 max-w-4xl mx-auto">
            {[
              {
                question: "What is the typical ROI of employee recognition programs?",
                answer: "Organizations typically see 3-5x ROI from recognition programs through reduced turnover costs (30-200% of salary savings), increased productivity (up to 21% improvement), and higher engagement scores. Most companies break even within 6-12 months."
              },
              {
                question: "How do you measure the success of employee recognition programs?",
                answer: "Key metrics include recognition frequency, participation rates, employee engagement scores, retention rates, productivity measures, and employee Net Promoter Score (eNPS). Track these quarterly for meaningful insights."
              },
              {
                question: "How does recognition impact employee retention?",
                answer: "Recognition programs can reduce voluntary turnover by 31-45%. Since 79% of employees who quit cite lack of appreciation as a key reason, regular recognition directly addresses this retention risk factor."
              },
              {
                question: "What business outcomes improve with employee recognition?",
                answer: "Recognition drives measurable improvements in employee engagement (31% higher), productivity (21% increase), customer satisfaction scores, safety incidents reduction, and overall profitability through reduced turnover costs."
              }
            ].map((faq, index) => (
              <Collapsible key={index}>
                <Card className="border-border/60 bg-card/50 backdrop-blur-sm">
                  <CollapsibleTrigger className="w-full group">
                    <CardHeader className="pb-4">
                      <CardTitle className="text-left flex items-center justify-between">
                        <span className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                          {faq.question}
                        </span>
                        <div className="flex items-center">
                          <Plus className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors group-data-[state=open]:hidden" />
                          <Minus className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors group-data-[state=closed]:hidden" />
                        </div>
                      </CardTitle>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <p className="text-muted-foreground leading-relaxed">
                        {faq.answer}
                      </p>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            ))}
          </div>
        </section>

        {/* Assembly Impact CTA Section */}
        <section>
          <Card className="bg-gradient-to-br from-primary/5 via-accent/5 to-secondary/5 border-primary/20 backdrop-blur-sm">
            <CardContent className="p-8 text-center space-y-6">
              <div className="space-y-3">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto border border-primary/20">
                  <CheckCircle className="w-8 h-8 text-primary" />
                </div>
                
                <h3 className="text-2xl font-bold text-foreground">
                  Assembly's Impact
                </h3>
                
                <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                  With Assembly, companies see measurable improvements fast:
                </p>
              </div>
              
              <div className="grid gap-4 text-left max-w-4xl mx-auto">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 shrink-0"></div>
                  <div>
                    <div className="font-medium text-foreground">Peer-to-peer recognition</div>
                    <div className="text-sm text-muted-foreground">boosts daily engagement and cross-team visibility</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 shrink-0"></div>
                  <div>
                    <div className="font-medium text-foreground">Automated milestone celebrations</div>
                    <div className="text-sm text-muted-foreground">increase retention touchpoints without adding admin work</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 shrink-0"></div>
                  <div>
                    <div className="font-medium text-foreground">Built-in analytics</div>
                    <div className="text-sm text-muted-foreground">track recognition frequency, participation rates, and business outcomes</div>
                  </div>
                </div>
              </div>
              
              <div className="pt-4 space-y-4">
                <div className="flex items-center gap-2 text-sm justify-center">
                  <ExternalLink className="w-4 h-4 text-primary" />
                  <a 
                    href="/blog/employee-recognition-engagement-trends" 
                    className="text-primary hover:text-primary/80 transition-colors font-medium underline underline-offset-2"
                  >
                    Employee Recognition Engagement Trends
                  </a>
                </div>
                
                <Button size="lg" asChild className="bg-primary hover:bg-primary/90 text-primary-foreground">
                  <a href="/book-demo" className="flex items-center gap-2">
                    See Assembly's Impact in Action
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Closing Statement */}
        <div className="text-center space-y-4 py-8">
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Recognition is more than a cultural nice-to-have-it's a <strong className="text-foreground">business strategy</strong>. When you make recognition part of everyday work, you strengthen both your people and your bottom line.
          </p>
        </div>
      </div>
    </RecognitionGuideLayout>
  );
}